import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GoUpload } from "react-icons/go";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import DeleteModal from "../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../components/popovers/actionPopover";
import {
	ALLOWANCE_FORMULA_TYPE,
	AllowanceDto,
} from "../../../../models/allowance.dto";
import { AiOutlinePlus } from "react-icons/ai";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import {
	useDeleteAllowanceGroup,
	useGetAllowanceGroup,
	useGetAllowanceGroupSearch,
} from "../../../../services/allowance-group.service";
import {
	useDeleteAllowance,
	useGetAllowance,
	useGetAllowanceSearch,
} from "../../../../services/allowance.service";
import UploadAllowances from "../modal/UploadAllowances";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import AllowanceGroupModalForm from "../modal/AllowanceGroupModalForm";
import EmployeeAllowanceModalForm from "../modal/AllowanceModalForm";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import { useGetCompanyCurrencyConfig } from "../../../../services/company-currency.service";
import { useGetEmployeeBand } from "../../../../services/employee-band.service";
import APISearchInput from "../../../../components/search/APISearchInput";
import { useGetPayPeriod } from "../../../../services/pay-period.service";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import { EmployeeBandOrderBy } from "../../../../models/employee-band.dto";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import { useGetEmployee } from "../../../../services/employee.service";
import {
	PermissionChecker,
	PERMISSIONS,
	useCheckUserPermissions,
} from "../../../../helpers/usePermissionManager";
import { ChevronDown, Upload } from "lucide-react";
import {
	allowanceGroupColumns,
	allowanceGroupColumnVisibility,
	allowanceColumns,
	allowanceColumnVisibility,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../models/table-configuration.dto";
import ExportAllowancesModal from "../modal/ExportAllowancesModal";
import { BsDownload } from "react-icons/bs";

const AllowancesView = () => {
	const { currentCompany } = useCompanyStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();

	// Tab state
	const [activeTab, setActiveTab] = useState<"allowanceGroups" | "allowances">(
		"allowanceGroups",
	);

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openAllowanceGroupModal, setOpenAllowanceGroupModal] = useState(false);
	const [openEmployeeAllowanceModal, setOpenEmployeeAllowanceModal] =
		useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [openExportModal, setOpenExportModal] = useState(false);
	const [openUploadAllowancesModal, setOpenUploadAllowancesModal] =
		useState(false);
	const [isUploadExportOpen, setIsUploadExportOpen] = useState(false);
	const uploadExportDropdownRef = useRef<HTMLDivElement | null>(null);

	const [searchParams, setSearchParams] = useSearchParams();

	// Column visibility for Allowance Groups
	const {
		columnVisibilityModel: allowanceGroupColumnVisibilityModel,
		setColumnVisibilityModel: setAllowanceGroupColumnVisibilityModel,
	} = useColumnVisibilityModel(allowanceGroupColumnVisibility);

	// Column visibility for Allowances
	const {
		columnVisibilityModel: allowanceColumnVisibilityModel,
		setColumnVisibilityModel: setAllowanceColumnVisibilityModel,
	} = useColumnVisibilityModel(allowanceColumnVisibility);

	// Allowance Groups table
	const { mutate: searchAllowanceGroups } = useGetAllowanceGroupSearch(
		currentCompany?.id as number,
	);
	const {
		tableAPIRef: allowanceGroupTableAPIRef,
		fetchedTableData: fetchedAllowanceGroupData,
		tableData: allowanceGroupTableData,
		isLoading: allowanceGroupLoading,
		pageCount: allowanceGroupPageCount,
		setTableData: setAllowanceGroupTableData,
	} = useManageTableWithSearchParams<AllowanceDto>({
		fetchHook: (options) =>
			useGetAllowanceGroup(currentCompany?.id as number, options),
		options: {},
	});

	// Allowances table
	const { mutate: searchAllowances } = useGetAllowanceSearch();
	const {
		tableAPIRef: allowanceTableAPIRef,
		fetchedTableData: fetchedAllowanceData,
		tableData: allowanceTableData,
		isLoading: allowanceLoading,
		pageCount: allowancePageCount,
		setTableData: setAllowanceTableData,
	} = useManageTableWithSearchParams<AllowanceDto>({
		fetchHook: useGetAllowance,
		options: {
			companyId: currentCompany?.id,
		},
	});

	// Fetched data for forms
	const { data: companyCurrencies } = useGetCompanyCurrencyConfig({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
	});

	const { data: allowanceGroups } = useGetAllowanceGroup(
		currentCompany?.id as number,
		{
			limit: APP_CONFIG.PAGE_LIMIT,
			orderBy: "createdAt:desc" as any,
		} as any,
	);
	const { data: payPeriods } = useGetPayPeriod({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: "createdAt:desc" as any,
	});
	const { data: employees } = useGetEmployee({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});
	const { data: employeeBands } = useGetEmployeeBand({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: EmployeeBandOrderBy.NAME_ASC,
	});

	const handleClose = () => setAnchorEl(null);

	const handleOpenAllowanceGroupModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenAllowanceGroupModal(true);
	};

	const handleOpenEmployeeAllowanceModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenEmployeeAllowanceModal(true);
	};

	const handleTabChange = (tab: "allowanceGroups" | "allowances") => {
		setActiveTab(tab);
		const updated = new URLSearchParams(searchParams);
		updated.delete("page");
		updated.delete("pageSize");
		setSearchParams(updated);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteAllowanceGroup } = useDeleteAllowanceGroup();
	const { mutate: deleteAllowance } = useDeleteAllowance();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		if (activeTab === "allowanceGroups") {
			deleteAllowanceGroup(
				{ companyId: currentCompany?.id as number, id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		} else {
			deleteAllowance(
				{ id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				uploadExportDropdownRef.current &&
				!uploadExportDropdownRef.current.contains(event.target as Node)
			) {
				setIsUploadExportOpen(false);
			}
		};

		if (isUploadExportOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isUploadExportOpen]);

	const handleOpenExportModal = () => {
		setOpenExportModal(true);
	};

	return (
		<>
			<div className="flex justify-between items-center mt-4 mb-4">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={
							activeTab === "allowanceGroups"
								? fetchedAllowanceGroupData
								: fetchedAllowanceData
						}
						setTableData={
							activeTab === "allowanceGroups"
								? setAllowanceGroupTableData
								: setAllowanceTableData
						}
						mutate={
							activeTab === "allowanceGroups"
								? searchAllowanceGroups
								: searchAllowances
						}
					/>
					<TableFilter
						fields={
							activeTab === "allowanceGroups"
								? [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Name", fieldName: "name" },
												{ title: "Code", fieldName: "code" },
												{ title: "Min Value", fieldName: "minValue" },
												{ title: "Max Value", fieldName: "maxValue" },
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Formula Type",
											fieldName: "formulaType",
											fieldType: "CHECKBOX_LIST",
											options: ALLOWANCE_FORMULA_TYPE,
										},
										{
											title: "Active",
											fieldName: "active",
											fieldType: "YES_NO",
										},
										{
											title: "Prorate",
											fieldName: "prorate",
											fieldType: "YES_NO",
										},
										{
											title: "Non-Taxable",
											fieldName: "nonTaxable",
											fieldType: "YES_NO",
										},
									]
								: [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Name", fieldName: "name" },
												{ title: "Code", fieldName: "code" },
												{ title: "Min Value", fieldName: "minValue" },
												{ title: "Max Value", fieldName: "maxValue" },
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Formula Type",
											fieldName: "formulaType",
											fieldType: "CHECKBOX_LIST",
											options: ALLOWANCE_FORMULA_TYPE,
										},
										{
											title: "Allowance Group",
											fieldName: "groupId",
											fieldType: "INPUT_FIELD",
											inputType: "number",
										},
										{
											title: "Active",
											fieldName: "active",
											fieldType: "YES_NO",
										},
										{
											title: "Prorate",
											fieldName: "prorate",
											fieldType: "YES_NO",
										},
										{
											title: "Non-Taxable",
											fieldName: "nonTaxable",
											fieldType: "YES_NO",
										},
									]
						}
					/>
					<ManageTableColumns
						columns={
							activeTab === "allowanceGroups"
								? (allowanceGroupColumns as any)
								: (allowanceColumns as any)
						}
						defaultVisibilityModel={
							activeTab === "allowanceGroups"
								? allowanceGroupColumnVisibility
								: allowanceColumnVisibility
						}
						visibilityModel={
							activeTab === "allowanceGroups"
								? allowanceGroupColumnVisibilityModel
								: allowanceColumnVisibilityModel
						}
						setVisibilityModel={
							activeTab === "allowanceGroups"
								? setAllowanceGroupColumnVisibilityModel
								: setAllowanceColumnVisibilityModel
						}
						tableApiRef={
							activeTab === "allowanceGroups"
								? allowanceGroupTableAPIRef
								: allowanceTableAPIRef
						}
					/>
				</div>
				<div className="flex items-center gap-4 font-medium">
					{/* Upload - Only show for Allowance Groups tab */}
					{activeTab === "allowanceGroups" && (
						<PermissionChecker
							permissionRule="OR"
							permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
						>
							<button
								onClick={() => setOpenUploadAllowancesModal(true)}
								className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
							>
								<GoUpload className="mr-2 text-[17px] text-gray-500" />
								Upload Allowances
							</button>
						</PermissionChecker>
					)}

					{/* Export - Only show for Employee Allowances tab */}
					{activeTab === "allowances" && (
						<button
							onClick={handleOpenExportModal}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						>
							<BsDownload className="mr-2 text-[18px] text-gray-500" />
							Export Allowances
						</button>
					)}

					<PermissionChecker
						permissionRule="OR"
						permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
					>
						{activeTab === "allowanceGroups" ? (
							<button
								onClick={handleOpenAllowanceGroupModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Allowance Group
							</button>
						) : (
							<button
								onClick={handleOpenEmployeeAllowanceModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Employee Allowance
							</button>
						)}
					</PermissionChecker>
				</div>
			</div>

			{/* Tabs */}
			<div className="flex border-b border-gray-200 mb-6">
				<button
					onClick={() => handleTabChange("allowanceGroups")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "allowanceGroups"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Allowance Groups
				</button>
				<button
					onClick={() => handleTabChange("allowances")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "allowances"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Employee Allowances
				</button>
			</div>

			{activeTab === "allowanceGroups" ? (
				<TanStackEmployeeTable
					ref={allowanceGroupTableAPIRef}
					tableId={TableId.ALLOWANCE_GROUPS}
					data={allowanceGroupTableData}
					columns={allowanceGroupColumns}
					loading={allowanceGroupLoading}
					columnVisibility={allowanceGroupColumnVisibilityModel}
					onColumnVisibilityChange={setAllowanceGroupColumnVisibilityModel}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.target as HTMLButtonElement);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget as HTMLButtonElement);
					}}
					initialColumnVisibility={allowanceGroupColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([
						PERMISSIONS.COMPANY_CONFIGS_READ,
					])}
					pageCount={allowanceGroupPageCount}
					apiRef={allowanceGroupTableAPIRef}
				/>
			) : (
				<TanStackEmployeeTable
					ref={allowanceTableAPIRef}
					tableId={TableId.ALLOWANCE}
					data={allowanceTableData}
					columns={allowanceColumns}
					loading={allowanceLoading}
					columnVisibility={allowanceColumnVisibilityModel}
					onColumnVisibilityChange={setAllowanceColumnVisibilityModel}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.COMPANY_CONFIGS_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.target as HTMLButtonElement);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget as HTMLButtonElement);
					}}
					initialColumnVisibility={allowanceColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([
						PERMISSIONS.COMPANY_CONFIGS_READ,
					])}
					pageCount={allowancePageCount}
					apiRef={allowanceTableAPIRef}
				/>
			)}

			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={
					activeTab === "allowanceGroups"
						? setOpenAllowanceGroupModal
						: setOpenEmployeeAllowanceModal
				}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.name || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openAllowanceGroupModal && (
				<AllowanceGroupModalForm
					open={openAllowanceGroupModal}
					setOpen={setOpenAllowanceGroupModal}
					title={
						isEditForm ? "Update Allowance Group" : "Add New Allowance Group"
					}
					isEditForm={isEditForm}
					tableAPIRef={allowanceGroupTableAPIRef}
					companyCurrencies={companyCurrencies?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openEmployeeAllowanceModal && (
				<EmployeeAllowanceModalForm
					open={openEmployeeAllowanceModal}
					setOpen={setOpenEmployeeAllowanceModal}
					title={
						isEditForm
							? "Update Employee Allowance"
							: "Add New Employee Allowance"
					}
					isEditForm={isEditForm}
					tableAPIRef={allowanceTableAPIRef}
					employees={employees?.data || []}
					employeeBands={employeeBands?.data || []}
					payPeriods={payPeriods?.data || []}
					allowanceGroups={allowanceGroups?.data || []}
					companyCurrencies={companyCurrencies?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openUploadAllowancesModal && (
				<UploadAllowances
					open={openUploadAllowancesModal}
					setOpen={setOpenUploadAllowancesModal}
				/>
			)}
			{openExportModal && currentCompany?.id && (
				<ExportAllowancesModal
					open={openExportModal}
					setOpen={setOpenExportModal}
					companyId={currentCompany.id}
				/>
			)}
		</>
	);
};

export default AllowancesView;
